import { Box, Divider } from '@mui/material'
import {
  TABLE_WIDTH_BRAND,
  TABLE_WIDTH_FOUND,
  TABLE_WIDTH_MODEL,
  TABLE_WIDTH_PARTNER,
  TABLE_WIDTH_PLATE,
} from 'lib/tableLib'
import T from 'prop-types'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import ButtonLink from 'components/atoms/ButtonLink'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DotCell from 'components/atoms/DotCell'
import DownloadBar from 'components/atoms/DownloadBar'
import DropArea from 'components/atoms/DropArea'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import Table from 'components/atoms/Table'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'
import UploadedFileName from 'components/atoms/UploadedFileName'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
  validateExpectedLabel,
  validateNotEmpty,
} from 'hooks/useInputOutput'

import SessionsDownloadTemplate from '../Sessions/SessionsDownloadTemplate'
import SessionsSearchBar from '../Sessions/SessionsSearchBar'

const createColumns = (t, cars, carsFilters) => {
  const {
    filters,
    filtersData,
    setBrands,
    setModels,
    setPartners,
    setSoldType,
    setExists,
    setPlateQuery,
  } = carsFilters

  return [
    {
      accessor: 'brand',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.brand')}
          filter={
            <FilterSelect
              options={filtersData.brands}
              value={filters.brands}
              handleChange={setBrands}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarBrand')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_BRAND,
        },
      },
    },
    {
      accessor: 'model',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.model')}
          filter={
            <FilterSelect
              options={filtersData.models}
              value={filters.models}
              handleChange={setModels}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarModel')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_MODEL,
        },
      },
    },
    {
      accessor: 'plate',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.plate')}
          filter={
            <SearchField
              data-testid="search-plate"
              placeholder={t('common.form.searchCarPlate')}
              value={filters.plateQuery}
              onChange={(event) => setPlateQuery(event.target.value)}
              fullWidth={false}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PLATE,
        },
      },
    },
    {
      accessor: 'partner',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.partner')}
          errorsNumber={cars.filter((car) => !car.soldPartnerName).length}
          filter={
            <FilterSelect
              options={filtersData.partners}
              value={filters.partners}
              handleChange={setPartners}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchPartner')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_PARTNER,
        },
      },
    },
    {
      accessor: 'status',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.status')}
          filter={
            <FilterSelect
              options={filtersData.soldType}
              value={filters.soldType}
              handleChange={setSoldType}
              fullWidth={false}
              sx={{ minWidth: 150 }}
            />
          }
        />
      ),
    },
    {
      accessor: 'exists',
      Header: (
        <TableHeaderCell
          label={t('sessions.sold.table.exists')}
          errorsNumber={
            cars.filter((car) => !car.exists || !car.existsInCars).length
          }
          filter={
            <FilterSelect
              options={filtersData.exists}
              value={filters.exists}
              handleChange={setExists}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_FOUND,
        },
      },
    },
  ]
}

const COMMON_ERROR_WRONG_COLUMN_NAME = 'common.error.fileUpload.wrongColumnName'
export default function SessionsSold({
  session,
  stepCars,
  updateSold,
  handleValidation,
  handleConfirm,
  // handleIngestSold,
  confirmNoSoldCars,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const { step } = useParams()
  const carsFilters = useCarsFilters({
    startFilters: {
      existsCheck: (car) => car.exists && car.existsInCars,
    },
  })
  const { filters, initFilters, setQuery, filterCars, resetFilters } =
    carsFilters

  useEffect(() => {
    if (!stepCars.length) {
      return
    }

    initFilters(stepCars)
  }, [stepCars, initFilters])

  const filteredCars = useMemo(
    () => filterCars(stepCars, filters),
    [stepCars, filterCars, filters]
  )

  const columns = useMemo(
    () => createColumns(t, stepCars, carsFilters),
    [t, stepCars, carsFilters]
  )

  const parser = (rows) => {
    validateExpectedLabel(
      rows,
      1,
      1,
      t('common.excel.plate'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )
    validateExpectedLabel(
      rows,
      1,
      2,
      t('common.excel.partner'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )
    validateExpectedLabel(
      rows,
      1,
      3,
      t('common.excel.status'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )

    return rows
      .slice(2)
      .map((row, i) => {
        const value =
          validateNotEmpty(rows, i + 2, 3) &&
          row[3].toString().trim().toLowerCase()

        return row.length && validateNotEmpty(rows, i + 2, 1)
          ? {
              plate: row[1].toString().trim().toUpperCase(),
              partnerId:
                (validateNotEmpty(rows, i + 2, 2) &&
                  row[2].toString().trim()) ||
                null,
              type:
                value &&
                value === t('common.excel.statusType.sold').toLowerCase()
                  ? 'sold'
                  : value ===
                    t('common.excel.statusType.preAssigned').toLowerCase()
                  ? 'preAssigned'
                  : value || null,
            }
          : null
      })
      .filter((item) => item)
  }

  const handleUpdate = useCallback(
    (cars, fileName) => {
      updateSold(session?.sessionId, cars, fileName)
      resetFilters()
    },
    [resetFilters, session?.sessionId, updateSold]
  )

  // const handleIngest = useCallback(() => {
  //   handleIngestSold(session?.sessionId)
  //   resetFilters()
  // }, [handleIngestSold, session?.sessionId, resetFilters])

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(
    handleUpdate,
    parser
  )

  const handleDownload = async () => {
    const rows = [
      [
        t('common.excel.plate'),
        t('common.excel.partner'),
        t('common.excel.status'),
        t('sessions.sold.table.exists'),
        t('common.excel.name'),
      ],
      ...stepCars.map((car) => [
        car.plate,
        car.soldPartnerId,
        t(`common.excel.statusType.${car.soldType}`),
        car.exists
          ? t('sessions.sold.carFoundOk')
          : t('sessions.sold.carFoundMissing'),
        `${car.title}`,
      ]),
    ]
    saveXls('purchased', rows)
  }

  const preparedItems = filteredCars.map((car) => {
    const exists = filters.existsCheck(car)

    return {
      ...car,
      partner: car.soldPartnerName,
      status: (
        <DotCell
          color={car.soldType === 'sold' ? 'primary' : 'warning'}
          label={t(`sessions.sold.status.${car.soldType}`)}
        />
      ),
      exists: (
        <DotCell
          color={exists ? 'primary' : 'error'}
          label={
            exists
              ? t('sessions.sold.carFoundOk')
              : t('sessions.sold.carFoundMissing')
          }
        />
      ),
    }
  })

  const handleConfirmNoSoldCars = useCallback(() => {
    stepCars.length ? confirmNoSoldCars(session.sessionId) : handleConfirm()
  }, [confirmNoSoldCars, handleConfirm, session.sessionId, stepCars.length])

  return (
    <div data-testid="session-sold">
      <TitleBar
        title={t(`sessions.sold.contentTitle`)}
        description={t(`sessions.sold.contentDescription`)}
        rightColumn={
          <>
            <UploadedFileName
              fileName={session?.[`${step}FileName`]}
              uploaded={!!stepCars.length}
            />
            <ButtonLink
              color="textPrimary"
              underline="none"
              onClick={handleConfirmNoSoldCars}
              disabled={confirmed || readOnly || contentIsReadOnly}
            >
              <strong>{t('sessions.sold.confirmNoSoldCars')}</strong>
            </ButtonLink>
            <Box pr={2} />
            <UploadBar
              id={step}
              fileName={session?.[`${step}FileName`]}
              readOnly={readOnly || contentIsReadOnly}
              uploaded={!!stepCars.length}
              handleEmptyList={() => handleUpdate(session.sessionId, [])}
              handleSelectFile={handleSelectFile}
              hideFileName
              // onIngest={handleIngest}
              // ingestLabel={t(`common.form.ingestButtonSold`)}
            />
          </>
        }
      />
      {stepCars.length ? (
        <>
          <Table
            id="cars-list"
            items={preparedItems}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t(`sessions.sold.confirmButton`)}
                  disabled={
                    !handleValidation() ||
                    confirmed ||
                    readOnly ||
                    contentIsReadOnly
                  }
                  left={
                    <SessionsSearchBar
                      id="text"
                      label={t('sessions.sold.platesUploaded')}
                      filteredLabel={t('common.filters.filteredPlates')}
                      cars={stepCars}
                      filteredCars={filteredCars}
                      query={filters.query}
                      handleChangeSearch={(event) =>
                        setQuery(event.target.value)
                      }
                    />
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <DownloadBar
                      readOnly={readOnly || contentIsReadOnly}
                      handleDownload={handleDownload}
                    />
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
          <SessionsDownloadTemplate
            label={t('sessions.sold.downloadTemplateFile')}
            handleDownload={handleDownload}
          />
        </>
      )}
    </div>
  )
}

SessionsSold.propTypes = {
  session: T.object.isRequired,
  stepCars: T.array.isRequired,
  updateSold: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  confirmNoSoldCars: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
